import { InfoOutlined } from '@mui/icons-material';
import { Chip, IconButton, Tooltip, darken, lighten } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getEmailSubscription, getUpdateSubscription } from 'src/shared/services/emailService';

type EmailSubscriptionHandlerProps = {
  email?: string;
};

export const EmailSubscriptionHandler = (props: EmailSubscriptionHandlerProps) => {
  const { email } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [unSubscribed, setUnsubscribed] = useState<boolean | undefined>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    if (email) {
      try {
        const res = await getEmailSubscription(email);
        if (isMounted()) {
          setUnsubscribed(res.data);
        }
      } catch (e) {
        ConsoleHelper.log(e);
      }
    }
  }, [email, isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubscription = async () => {
    if (email && unSubscribed !== undefined) {
      try {
        const activateSubscription = !unSubscribed;
        await getUpdateSubscription(email, activateSubscription);
        if (isMounted()) {
          fetchData();
        }
      } catch (e) {
        ConsoleHelper.log(e);
      } finally {
        if (isMounted()) {
          setOpenDeleteDialog(false);
        }
      }
    }
  };

  return (
    <>
      {unSubscribed && (
        <IconButton sx={{ marginLeft: 1 }} onClick={() => setOpenDeleteDialog(true)}>
          <Tooltip title={t('member:emailSubscription.unsubscribedDetailed')} arrow placement='right-start'>
            <Chip
              icon={<InfoOutlined fontVariant='outlined' />}
              label={t('member:emailSubscription.unsubscribed')}
              sx={{
                backgroundColor: (thm) => lighten(thm.palette.warning.main, 0.8),
                color: (thm) => darken(thm.palette.warning.main, 0.5),
                fontSize: (thm) => thm.typography.caption.fontSize,
                fontWeight: (thm) => thm.typography.fontWeightMedium,
              }}
              size='small'
            />
          </Tooltip>
        </IconButton>
      )}
      <DeleteConfirmationDialog
        title={!unSubscribed ? t('member:emailSubscription.confirmUnsubscribeTitle') : t('member:emailSubscription.confirmReactivateTitle')}
        value=''
        modaltext={!unSubscribed ? t('member:emailSubscription.confirmUnsubscribeText') : t('member:emailSubscription.confirmReactivateText')}
        onClose={(value) => {
          if (value) {
            handleSubscription();
          } else {
            setOpenDeleteDialog(false);
          }
        }}
        open={openDeleteDialog}
      />
    </>
  );
};
